type AlgoliaSearchLike = {
  search: (
    query: string,
    params?: Record<string, unknown>
  ) => Promise<{ hits: unknown[]; nbPages: number; nbHits: number }>;
};

export type CollectFilteredHitsOptions<T> = {
  searchParams?: Record<string, unknown>;
  filterHits: (hits: T[], query: string) => T[];
  limit: number;
  offset?: number;
  fetchHitsPerPage?: number;
  maxPagesToScan?: number;
};

export type CollectFilteredHitsResult<T> = {
  hits: T[];
  /** Total filtered hits when every Algolia page was scanned; otherwise null. */
  filteredTotal: number | null;
  hasMore: boolean;
  exhausted: boolean;
  algoliaNbHits: number;
};

/**
 * Walk Algolia pages, apply a client-side hit filter, and return a window of
 * filtered hits so a single filtered page cannot appear empty when later
 * Algolia pages still contain keepable matches.
 */
export async function collectFilteredHits<T>(
  index: AlgoliaSearchLike,
  query: string,
  options: CollectFilteredHitsOptions<T>
): Promise<CollectFilteredHitsResult<T>> {
  const {
    searchParams = {},
    filterHits,
    limit,
    offset = 0,
    fetchHitsPerPage = Math.max(limit * 3, 30),
    maxPagesToScan = 10
  } = options;

  const needed = offset + limit;
  const collected: T[] = [];
  let algoliaPage = 0;
  let algoliaNbPages = 1;
  let algoliaNbHits = 0;

  // Keep scanning past the soft cap until the requested offset is reachable or Algolia is exhausted.
  const hardCap = Math.max(maxPagesToScan, Math.ceil((offset + 1) / Math.max(fetchHitsPerPage, 1)) + maxPagesToScan);

  while (collected.length < needed && algoliaPage < algoliaNbPages && algoliaPage < hardCap) {
    const response = await index.search(query, {
      ...searchParams,
      hitsPerPage: fetchHitsPerPage,
      page: algoliaPage
    });

    algoliaNbPages = response.nbPages;
    algoliaNbHits = response.nbHits;
    collected.push(...filterHits(response.hits as T[], query));
    algoliaPage += 1;

    // Soft cap: once the offset window is fillable, stop early if we already have a full page.
    if (algoliaPage >= maxPagesToScan && collected.length >= needed) {
      break;
    }
  }

  const exhausted = algoliaPage >= algoliaNbPages;
  const hits = collected.slice(offset, offset + limit);
  // Only advertise another page when we already have a surplus, or we filled this
  // window without hitting the hard scan cap (so later Algolia pages are still reachable).
  const hitScanCap = !exhausted && algoliaPage >= hardCap;
  const hasMore = collected.length > offset + limit || (!exhausted && !hitScanCap && collected.length >= needed);

  return {
    hits,
    filteredTotal: exhausted ? collected.length : null,
    hasMore,
    exhausted,
    algoliaNbHits
  };
}
